/**
 * DropdownHandler - Modern vanilla JS dropdown implementation
 * Handles toggle, keyboard navigation, and outside-click closing
 */

class DropdownHandler {
    constructor() {
        this.dropdowns = Array.from(document.querySelectorAll(".dropdown"));
        this.openDropdowns = new Set();
        this.currentFocusedItem = null;

        if (this.dropdowns.length > 0) {
            this.initializeDropdowns();
        }
        this.attachGlobalListeners();
    }

    // ========================================
    // Initialization
    // ========================================

    initializeDropdowns() {
        this.dropdowns.forEach(dropdown => {
            const toggle = dropdown.querySelector(".dropdown-toggle");
            const menu = dropdown.querySelector(".dropdown-menu");

            if (!toggle || !menu) return;

            // Attach click listener to toggle
            toggle.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggle(dropdown);
            });

            // Store references for keyboard navigation
            dropdown.dropdownMenu = menu;
            dropdown.dropdownToggle = toggle;
        });
    }

    attachGlobalListeners() {
        // Close dropdown when clicking outside
        document.addEventListener("click", (e) => {
            this.dropdowns.forEach(dropdown => {
                if (!dropdown.contains(e.target) && this.openDropdowns.has(dropdown)) {
                    this.close(dropdown);
                }
            });
        });

        // Keyboard navigation (ESC, arrow keys)
        document.addEventListener("keydown", (e) => {
            if (this.openDropdowns.size === 0) return;

            if (e.key === "Escape") {
                // Capture currently open dropdowns before closing so we can focus their toggles
                const openBeforeClose = Array.from(this.openDropdowns);
                openBeforeClose.forEach(dropdown => this.close(dropdown));
                openBeforeClose.forEach(dropdown => {
                    dropdown.dropdownToggle?.focus();
                });
            } else if (["ArrowDown", "ArrowUp"].includes(e.key)) {
                e.preventDefault();
                this.handleArrowNavigation(e.key);
            }
        });
    }

    register(dropdownEl) {
        const toggle = dropdownEl.querySelector(".dropdown-toggle");
        const menu = dropdownEl.querySelector(".dropdown-menu");

        if (!toggle || !menu) return;

        toggle.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggle(dropdownEl);
        });

        dropdownEl.dropdownMenu = menu;
        dropdownEl.dropdownToggle = toggle;

        this.dropdowns.push(dropdownEl);
    }

    // ========================================
    // Toggle & State Management
    // ========================================

    toggle(dropdown) {
        if (this.openDropdowns.has(dropdown)) {
            this.close(dropdown);
        } else {
            this.open(dropdown);
        }
    }

    open(dropdown) {
        const menu = dropdown.dropdownMenu;
        if (!menu) return;

        // Ensure only one dropdown is open at a time: close any others first
        const others = Array.from(this.openDropdowns).filter(d => d !== dropdown);
        others.forEach(d => this.close(d));

        dropdown.classList.add("open");
        menu.classList.add("open");
        this.openDropdowns.add(dropdown);

        // Update aria attributes
        dropdown.dropdownToggle?.setAttribute("aria-expanded", "true");
        menu.setAttribute("aria-hidden", "false");

        // Focus first menu item for keyboard nav
        this.currentFocusedItem = null;
    }

    close(dropdown) {
        const menu = dropdown.dropdownMenu;
        if (!menu) return;

        dropdown.classList.remove("open");
        menu.classList.remove("open");
        this.openDropdowns.delete(dropdown);

        // Update aria attributes
        dropdown.dropdownToggle?.setAttribute("aria-expanded", "false");
        menu.setAttribute("aria-hidden", "true");

        // Clear focused item
        if (this.currentFocusedItem?.closest(".dropdown-menu") === menu) {
            this.currentFocusedItem = null;
        }
    }

    // ========================================
    // Keyboard Navigation
    // ========================================

    handleArrowNavigation(direction) {
        // Get the first open dropdown (assume we're navigating in that)
        const openDropdown = Array.from(this.openDropdowns)[0];
        if (!openDropdown) return;

        const menu = openDropdown.dropdownMenu;
        const items = Array.from(menu.querySelectorAll('a, button, [role="menuitem"]'));

        if (items.length === 0) return;

        let nextItem;

        if (!this.currentFocusedItem) {
            // Focus first item on first arrow press
            nextItem = direction === "ArrowDown" ? items[0] : items[items.length - 1];
        } else {
            const currentIndex = items.indexOf(this.currentFocusedItem);
            if (direction === "ArrowDown") {
                nextItem = items[(currentIndex + 1) % items.length];
            } else {
                nextItem = items[(currentIndex - 1 + items.length) % items.length];
            }
        }

        if (nextItem) {
            this.currentFocusedItem = nextItem;
            nextItem.focus();
        }
    }
}

// ========================================
// Auto-Initialization
// ========================================

let dropdownHandler;

document.addEventListener("DOMContentLoaded", () => {
    dropdownHandler = new DropdownHandler();
});

export { dropdownHandler };
export default DropdownHandler;
